import React, { useEffect, useState } from 'react'
import useItemStore from '@store/useItemStore'
import itemService from '@services/itemService'
import {
  Plus,
  Eye,
  Edit2,
  Trash2,
  RotateCcw,
  Download,
  History,
  Users,
  FileText,
  List,
  BookOpen,
  Folder,
  Filter,
  Archive,
} from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader } from '@components/common'
import ItemForm from './ItemForm'
import ItemView from './ItemView'
import ItemVersions from './ItemVersions'
import ItemAssign from './ItemAssign'
import './Items.css'

/**
 * Items Repository Page (Super Admin)
 *
 * Manages Policies, Procedures, Templates, Guidelines, and Resources
 * with versioning and company distribution capabilities.
 */
const Items = () => {
  const {
    items,
    loading,
    filters,
    pagination,
    stats,
    fetchItems,
    fetchStats,
    selectItem,
    deleteItem,
    restoreItem,
    downloadItem,
    setSearch,
    setTypeFilter,
    setStatusFilter,
    nextPage,
    prevPage,
  } = useItemStore()

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showVersionsModal, setShowVersionsModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [actionType, setActionType] = useState(null)
  const [selectedItemForAction, setSelectedItemForAction] = useState(null)

  // Fetch data on mount and when filters/pagination change
  useEffect(() => {
    fetchItems()
    fetchStats()
  }, [filters, pagination.page, pagination.limit])

  const handleCreate = () => {
    selectItem(null)
    setShowCreateModal(true)
  }

  const handleView = (item) => {
    selectItem(item)
    setShowViewModal(true)
  }

  const handleEdit = (item) => {
    selectItem(item)
    setShowEditModal(true)
  }

  const handleVersions = (item) => {
    selectItem(item)
    setShowVersionsModal(true)
  }

  const handleAssign = (item) => {
    selectItem(item)
    setShowAssignModal(true)
  }

  const handleDownload = (item) => {
    downloadItem(item.id)
  }

  const handleDelete = (item) => {
    setSelectedItemForAction(item)
    setActionType('delete')
    setShowConfirmDialog(true)
  }

  const handleRestore = (item) => {
    setSelectedItemForAction(item)
    setActionType('restore')
    setShowConfirmDialog(true)
  }

  const confirmAction = async () => {
    if (!selectedItemForAction) return
    try {
      if (actionType === 'delete') {
        await deleteItem(selectedItemForAction.id)
      } else if (actionType === 'restore') {
        await restoreItem(selectedItemForAction.id)
      }
      setShowConfirmDialog(false)
      setSelectedItemForAction(null)
      setActionType(null)
    } catch (error) {
      console.error('Action failed:', error)
    }
  }

  const cancelAction = () => {
    setShowConfirmDialog(false)
    setSelectedItemForAction(null)
    setActionType(null)
  }

  /**
   * Pluralize type names correctly
   */
  const pluralizeType = (type) => {
    const plurals = {
      policy: 'Policies',
      procedure: 'Procedures',
      template: 'Templates',
      guideline: 'Guidelines',
      resource: 'Resources',
    }
    return plurals[type] || `${type.charAt(0).toUpperCase() + type.slice(1)}s`
  }

  const getTypeIcon = (type) => {
    const icons = {
      policy: <FileText size={16} />,
      procedure: <List size={16} />,
      template: <FileText size={16} />,
      guideline: <BookOpen size={16} />,
      resource: <Folder size={16} />,
    }
    return icons[type] || <FileText size={16} />
  }

  const getTypeColor = (type) => {
    const colors = {
      policy: '#3b82f6',
      procedure: '#10b981',
      template: '#8b5cf6',
      guideline: '#f59e0b',
      resource: '#6366f1',
    }
    return colors[type] || '#6b7280'
  }

  const getStatusBadge = (status, deletedAt) => {
    if (deletedAt) {
      return <span className="badge badge--deleted">Deleted</span>
    }
    const badges = {
      draft: <span className="badge badge--draft">Draft</span>,
      published: <span className="badge badge--published">Published</span>,
      archived: <span className="badge badge--archived">Archived</span>,
    }
    return badges[status] || null
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const columns = [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (item) => (
        <div className="item-cell">
          <div
            className="item-cell__icon"
            style={{ backgroundColor: `${getTypeColor(item.item_type)}20`, color: getTypeColor(item.item_type) }}
          >
            {getTypeIcon(item.item_type)}
          </div>
          <div className="item-cell__content">
            <div className="item-cell__title">{item.title}</div>
            <div className="item-cell__meta">
              {item.original_name} • {itemService.formatFileSize(item.file_size)}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'item_type',
      label: 'Type',
      sortable: true,
      align: 'center',
      render: (item) => (
        <span
          className="type-badge"
          style={{ backgroundColor: `${getTypeColor(item.item_type)}20`, color: getTypeColor(item.item_type) }}
        >
          {item.item_type.charAt(0).toUpperCase() + item.item_type.slice(1)}
        </span>
      ),
    },
    {
      key: 'current_version',
      label: 'Version',
      sortable: true,
      align: 'center',
      render: (item) => <span className="version-badge">v{item.current_version}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      align: 'center',
      render: (item) => getStatusBadge(item.status, item.deleted_at),
    },
    {
      key: 'assignment_count',
      label: 'Assigned',
      sortable: false,
      align: 'center',
      render: (item) => (
        <span className="assignment-count">
          <Users size={14} />
          {item.assignment_count || 0}
        </span>
      ),
    },
    {
      key: 'updated_at',
      label: 'Updated',
      sortable: true,
      align: 'right',
      render: (item) => formatDate(item.updated_at),
    },
  ]

  const actions = [
    { label: 'View', icon: <Eye size={16} />, onClick: handleView, variant: 'info' },
    { label: 'Download', icon: <Download size={16} />, onClick: handleDownload, variant: 'success', show: (item) => !item.deleted_at },
    { label: 'Edit', icon: <Edit2 size={16} />, onClick: handleEdit, variant: 'warning', show: (item) => !item.deleted_at },
    { label: 'Versions', icon: <History size={16} />, onClick: handleVersions, variant: 'secondary', show: (item) => !item.deleted_at },
    { label: 'Assign', icon: <Users size={16} />, onClick: handleAssign, variant: 'primary', show: (item) => !item.deleted_at && item.status === 'published' },
    { label: 'Delete', icon: <Trash2 size={16} />, onClick: handleDelete, variant: 'danger', show: (item) => !item.deleted_at },
    { label: 'Restore', icon: <RotateCcw size={16} />, onClick: handleRestore, variant: 'success', show: (item) => item.deleted_at },
  ]

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    ...itemService.getItemTypes().map((t) => ({ value: t.value, label: t.label })),
  ]

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    ...itemService.getStatusOptions().map((s) => ({ value: s.value, label: s.label })),
  ]

  return (
    <div className="items-page">
      <PageHeader
        title="Items Repository"
        description="Manage policies, procedures, templates, and resources with versioning"
        breadcrumbs={[{ label: 'Items Repository' }]}
        actions={
          <Button variant="primary" onClick={handleCreate}>
            <Plus size={20} />
            <span>Add Item</span>
          </Button>
        }
      />

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--primary"><Archive size={24} /></div>
          <div className="stat-card__content">
            <div className="stat-card__label">Total Items</div>
            <div className="stat-card__value">{stats.total || 0}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--success"><FileText size={24} /></div>
          <div className="stat-card__content">
            <div className="stat-card__label">Published</div>
            <div className="stat-card__value">{stats.published || 0}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--warning"><Edit2 size={24} /></div>
          <div className="stat-card__content">
            <div className="stat-card__label">Drafts</div>
            <div className="stat-card__value">{stats.drafts || 0}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--secondary"><Folder size={24} /></div>
          <div className="stat-card__content">
            <div className="stat-card__label">Archived</div>
            <div className="stat-card__value">{stats.archived || 0}</div>
          </div>
        </div>
      </div>

      {stats.byType && Object.keys(stats.byType).length > 0 && (
        <div className="type-distribution">
          {Object.entries(stats.byType).map(([type, count]) => (
            <div key={type} className="type-distribution__item" style={{ borderColor: getTypeColor(type) }}>
              <div className="type-distribution__icon" style={{ backgroundColor: `${getTypeColor(type)}20`, color: getTypeColor(type) }}>
                {getTypeIcon(type)}
              </div>
              <div className="type-distribution__content">
                <div className="type-distribution__label">{pluralizeType(type)}</div>
                <div className="type-distribution__value">{count}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="filters-section">
        <SearchBar value={filters.search} onChange={setSearch} placeholder="Search items..." loading={loading} />
        <div className="filters-section__divider"><Filter size={18} /></div>
        <div className="filters-section__controls">
          <Select value={filters.type} onChange={(e) => setTypeFilter(e.target.value)} options={typeOptions} />
          <Select value={filters.status} onChange={(e) => setStatusFilter(e.target.value)} options={statusOptions} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        actions={actions}
        loading={loading}
        emptyMessage="No items found. Create your first item to get started."
        onRowClick={handleView}
      />

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <div className="pagination__info">
            Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </div>
          <div className="pagination__controls">
            <Button variant="secondary" onClick={prevPage} disabled={pagination.page === 1 || loading} size="small">Previous</Button>
            <Button variant="secondary" onClick={nextPage} disabled={pagination.page >= pagination.totalPages || loading} size="small">Next</Button>
          </div>
        </div>
      )}

      {showCreateModal && <ItemForm mode="create" onClose={() => setShowCreateModal(false)} />}
      {showEditModal && <ItemForm mode="edit" onClose={() => setShowEditModal(false)} />}
      {showViewModal && (
        <ItemView
          onClose={() => setShowViewModal(false)}
          onEdit={() => { setShowViewModal(false); setShowEditModal(true) }}
          onVersions={() => { setShowViewModal(false); setShowVersionsModal(true) }}
          onAssign={() => { setShowViewModal(false); setShowAssignModal(true) }}
        />
      )}
      {showVersionsModal && <ItemVersions onClose={() => setShowVersionsModal(false)} />}
      {showAssignModal && <ItemAssign onClose={() => setShowAssignModal(false)} />}

      {showConfirmDialog && selectedItemForAction && (
        <ConfirmDialog
          isOpen={true}
          onConfirm={confirmAction}
          onCancel={cancelAction}
          title={actionType === 'delete' ? 'Delete Item?' : 'Restore Item?'}
          message={
            actionType === 'delete'
              ? `Are you sure you want to delete "${selectedItemForAction.title}"? This will archive the item but preserve all data.`
              : `Are you sure you want to restore "${selectedItemForAction.title}"? This will make it available again.`
          }
          confirmText={actionType === 'delete' ? 'Delete' : 'Restore'}
          variant={actionType === 'delete' ? 'danger' : 'info'}
        />
      )}
    </div>
  )
}

export default Items
